import { TypedEventEmitter } from "../connect/typedEventEmitter";
import databaseService = require("../databaseService");
import { IFilterReference, IParam, MxObject, SortSpecifies, TMapLookup } from "../interfaces";
import * as MendixApi from "../mendixApi";

export abstract class AbstractEntityHandler {
    private _onUpdated = new TypedEventEmitter<string>();
    /**
     * Data is Id of updated Entity
     */
    public onEntityUpdated = this._onUpdated.listenerAppender;
    private _onDeleted = new TypedEventEmitter<string>();
    /**
     * Data is Id of deleted Entity
     */
    public onEntityDeleted = this._onDeleted.listenerAppender;
    private _onEntityCreated = new TypedEventEmitter<MxObject>();
    /**
     * Data is New Mendix Object
     */
    public onEntityCreated = this._onEntityCreated.listenerAppender;
    /**
     * Function to return boolean to specify if skip update entity.
     * There are cases that the update of entity doesn't require to update,
     * so for optimization, we config this entity so
     * the Handler knows and skip the update(which require retrieve).Undefined to check
     */
    protected skipUpdateEntity: () => boolean | undefined = () => undefined;
    protected _entityLookup: TMapLookup<MxObject> = new Map();
    protected filterAttributes: string[] = [];
    /**
     * Attached entitiy is the entity which only be used in this handler.
     * For example, TreeEntity will have Entity (in datamodel) as "Attachted Entity"
     * because it is only existed in the context of TreeEntiy
     * (So we will not create an EntityHandler for Entity).
     */
    protected _attachted_entity: string = "";
    protected _attachted_entity_reference: string = "";
    protected _attachted_entity_attributes: string[] = [];

    protected _required_associations: string[] = [];

    protected _subcribeHandlers: TMapLookup<any> = new Map();
    /**
     * this property is to store the association from the entity to other entity Id throught the association name
     * Suppose we have A---->B, A---->C
     *  this will have format of {"A-B":{AId->B1Id, AId->B2Id},"A-C":...}
     */
    protected _association_refId_entId_map_map: TMapLookup<TMapLookup<string>> = new Map();
    /**
     * this property is to store the association from the entity to other entities throught the association name
     * Suppose we have A---->B, A---->C
     *  this will have format of {"A-B":{AId->B1Objs[]},"A-C":...}
     */
    protected _xpath_Id_MxObjs_map_map: TMapLookup<TMapLookup<MxObject[]>> = new Map();
    // _entity: mendix entity identifier
    // _entityPath: path to use for lookup
    // _isConstraintPath: path to use for a lookup
    constructor(protected _entity: string, protected _entityPath: string, protected _isConstraintPath: boolean) { }
    public setSkipUpdateEntity(skipFunction: () => boolean) { this.skipUpdateEntity = skipFunction; }
    public setFilterAttributes(values: string[]) { this.filterAttributes = values; }
    public configAttachedEntity(attachedEntity: string, attachedEntityRef: string, attachedEntityAttributes: string[]) {
        this._attachted_entity = attachedEntity;
        this._attachted_entity_attributes = attachedEntityAttributes;
        this._attachted_entity_reference = attachedEntityRef;
    }
    /**
     * If we config reference, mendix only retrieve what we sepecify, so we have to config what we need
     */
    public addRequiredAssociation(association: string) {
        this._required_associations.push(association);
    }
    protected hasAttachtedEntity(): boolean {
        return !!(this._attachted_entity && this._attachted_entity_attributes && this._attachted_entity_reference) ||
            (this._required_associations.length > 0);
    }

    protected generateFilterReferences(): IFilterReference {
        if (this.hasAttachtedEntity()) {
            const references: IFilterReference = {};
            if (this._attachted_entity) {
                references[this._attachted_entity_reference] = { attributes: this._attachted_entity_attributes };
            }
            this._required_associations.forEach(association => references[association] = { attributes: [] });
            return references;
        }
        return {};
    }
    public getAttachtedEntity(entityId: string): any {
        const entity = this._entityLookup.get(entityId);
        if (entity) {
            if (this.hasAttachtedEntity()) {
                const attachedEntity = entity.get(this._attachted_entity_reference) as any;
                return attachedEntity.attributes;
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    }
    /**
     * Load all instances of the configured entity by a path.
     */
    public async loadData(
        refObjIds: string[], otherContrains?: string[], sortSpec?: SortSpecifies, limit?: number
    ): Promise<MxObject[] | undefined> {
        let entities: MxObject[] | string | undefined;
        if (this._isConstraintPath) {
            entities = await databaseService.retrieveEntityByConstraint(
                this._entity, this._entityPath, refObjIds[0], otherContrains,
                this.filterAttributes, this.generateFilterReferences(), sortSpec, limit
            );
        } else {
            entities = await databaseService.retrieveEntityByAssociation(
                this._entity, this._entityPath, refObjIds,
                this.filterAttributes, this.generateFilterReferences(), sortSpec, limit
            );
        }
        if (entities) {
            if (!Array.isArray(entities)) { entities = []; }
            this.updateLookupMapFromArray(entities);
            this.subscribeEntities(entities);
            return entities;
        } else {
            MendixApi.throwError("Failed to load");
            return undefined;
        }
    }
    /**
     * Load referenced entity through association
     * @param otherEntity
     * @param association
     */
    public fillManyToOneAssociation(otherEntity: string, association: string): Promise<any> {
        const associationMap: TMapLookup<string> = new Map();
        this._association_refId_entId_map_map.set(association, associationMap);
        const entitiesArray = this._entityLookup.values();
        const promises: Array<Promise<any>> = Array.from(entitiesArray).map(entity =>
            databaseService.retrieveEntityByAssociation(otherEntity, association, [entity.getGuid()], undefined, {})
                .then(associatedEntities => {
                    this.checkEntityAndAddToAssociationMap(entity, associatedEntities, associationMap);
                }));
        return Promise.all(promises);
    }
    /**
     * Function to retrieve referenced entity from entity of this handler by constraint
     * then put it in a map with format {entityId->referencedEntities[]}
     */
    public fillOneToReferenceSet(otherEntity: string, constraint: string, filters: string[]): Promise<any> {
        const xpathMap: TMapLookup<MxObject[]> = new Map();
        this._xpath_Id_MxObjs_map_map.set(constraint, xpathMap);
        const removeLastElm: (value: string) => string = i => {
            const j = i.split("/");
            j.pop();
            const result = j.join("/") + "='[%CurrentObject%]'";
            return result;
        };
        const xPath = constraint.split(" or ").map(removeLastElm).join(" or ") + "]";
        const entitiesArray = this._entityLookup.values();
        const promises: Array<Promise<any>> = Array.from(entitiesArray)
            .map(async entity => {
                const entityId = entity.getGuid();
                const associatedEntities = await databaseService.retrieveEntityByConstraint(
                    otherEntity, xPath, entityId, undefined, filters, {}
                );
                return associatedEntities && xpathMap.set(entityId, associatedEntities);
            });
        return Promise.all(promises);
    }
    /**
     * Create, Commit then set the initialized values. Return new created object
     * - sepecializationType: one of specialization of this entityType
     * - initializeNameValues dictionary of (entity attribute name -> init value).
     * These values will be set right after we commit object
     * - commit: if yes, commit and otherwise
     */
    public async createThenCommitEntity(
        specialziationType?: string, initializeNameValues?: TMapLookup<any>, commit?: boolean
    ): Promise<MxObject | undefined> {
        const entityTypeToCreate = specialziationType ? specialziationType : this._entity;
        const newEntity = await MendixApi.createNewObject(entityTypeToCreate);
        if (newEntity) {
            const newEntityId = newEntity.getGuid();
            this.subscribeEntity(newEntityId);
            const paramList: IParam[] = [];
            if (initializeNameValues) {
                Array.from(initializeNameValues.keys()).forEach(attributeName => {
                    if (!newEntity.isReadonlyAttr(attributeName)) {// only set if not read only
                        paramList.push({
                            paramName: attributeName,
                            paramValue: initializeNameValues.get(attributeName)
                        });
                    }
                });
            }
            if (commit) {
                const updatedEntity = await MendixApi.commitObject(newEntity, paramList);
                if (updatedEntity) {// if success
                    this.updateLookupMap(newEntity);
                    return updatedEntity;
                } else {// if fail
                    this.unsubscribeEntity(newEntityId);
                    MendixApi.throwError("Failed to commit");
                }
            }
        } else {
            MendixApi.throwError("Failed to create");
        }
        return newEntity;
    }
    /**
     * Implement this function to set the new attribute of the object then commit.
     * @param id Id of entity has new data
     * @param changedInfo A map of change from ChangedAttributeName => NewValue
     */
    public abstract async setThenCommitEntity(
        id: string, changedInfo: TMapLookup<string | number | boolean | any>
    ): Promise<MxObject | undefined>;

    /** Destroy */
    public destroy() {
        this._onDeleted.off();
        this._onUpdated.off();
        this._onEntityCreated.off();
        this.unsubscribeAll();
        this._entityLookup.clear();
        this._association_refId_entId_map_map.clear();
        this._xpath_Id_MxObjs_map_map.clear();
    }
    /**
     * Update an entity based on guid.
     * - ForceUpdate will trigger the update entity even value of skipUpdateEntity is true.
     * It is used in case we need to get most uptodate version of entity to open form in readonly mode
     * - If entity is existed in database, retrieve then update it
     * - othewise, delete entity WITHOUT DELETE SUBSCRIPTION
     * - if resolve an undefined, it means that we skip
     * - otherwise, return id
     *
     */
    public async updateEntity(guid: string, forceUpdate: boolean = false): Promise<string | undefined> {
        if (forceUpdate || (!this.skipUpdateEntity || (this.skipUpdateEntity && !this.skipUpdateEntity()))) {
            // console.log("Store: update " + this._entity + " forceToUpdate: " + forceUpdate);
            const entity = await MendixApi.retrieveObjectById(guid);
            if (entity && !(entity as any).isNew()) {
                this.updateLookupMap(entity);
            } else if (entity && (entity as any).isNew()) {
                return undefined;
            } else {
                /**
                 * Here we delete the entity but not unsubscribe to listen to its change
                 * Reason: if we create a new object then open the new/edit form. In the form, if we refresh the object.
                 * Then we cannot retrieve the object by the previous retrieve.
                 * So we will delete the enty by guid (the next command).
                 * If we unsubscribe, in the delete, if we commit the object by click save,
                 * then we cannot get the signal that the object
                 * is changed in order to refresh it in the canvas. So we keep the subscribe here.
                 * The subscription will be remove anyway when uninitialize the widget.
                 */
                this.deleteEntity(guid, false);
            }
            return guid;
        } else {
            // console.log("Store: skip update entity " + this._entity);
            return undefined;
        }
    }
    protected updateLookupMapFromArray(entities: MxObject[]) {
        entities.forEach((entity: MxObject) => {
            this.updateLookupMap(entity);
        });
    }
    protected updateLookupMap(entity: MxObject) {
        const guid: string = entity.getGuid();

        if (this._entityLookup.get(guid)) {
            this.updateExistingEntity(entity, guid);
        } else {
            this.addNewEntity(entity);
        }
    }
    protected addNewEntity(newEntity: MxObject) {
        this._entityLookup.set(newEntity.getGuid(), newEntity);
        this._onEntityCreated.emit(newEntity);
    }
    protected updateExistingEntity(newEntity: MxObject, guid: string) {
        this._entityLookup.set(guid, newEntity);
        this._onUpdated.emit(newEntity.getGuid());
    }
    protected deleteEntity(entityId: string, willSubscribe: boolean = true) {
        // console.log("store: " + this._entity + " is deleted. Update the store");
        this._entityLookup.delete(entityId);
        if (willSubscribe) {
            this.unsubscribeEntity(entityId);
        }
        this._onDeleted.emit(entityId);
    }

    protected checkEntityAndAddToAssociationMap(
        entity: MxObject, associatedEntities: MxObject[], associationMap: TMapLookup<string>
    ) {
        if (associatedEntities[0]) { associationMap.set(entity.getGuid(), associatedEntities[0].getGuid()); }
    }

    public getRefercenceSetMapLookup(constraint: string): TMapLookup<MxObject[]> | undefined {
        return this._xpath_Id_MxObjs_map_map.get(constraint);
    }
    public getAssociationMapLookup(association: string): TMapLookup<string> | undefined {
        return this._association_refId_entId_map_map.get(association);
    }
    public getEntityMap(): TMapLookup<MxObject> {
        return this._entityLookup;
    }
    public getEntities() {
        return Array.from(this._entityLookup.values());
    }
    public has(id: string): boolean {
        return this._entityLookup.has(id);
    }
    public getFirstEntity(): MxObject | undefined {
        return Array.from(this._entityLookup.values())[0];
    }
    protected subscribeEntities(entities: MxObject[]) {
        entities.forEach((entity: MxObject) => {
            this.subscribeEntity(entity.getGuid());
        });
    }
    public subscribeEntity(entityId: string) {
        if (this._subcribeHandlers.get(entityId) === undefined) {
            this._subcribeHandlers.set(
                entityId, MendixApi.subscribe(entityId, () => this.onEntityChanged(entityId))
            );
        }
    }
    /**
     * Implement this function in concreted class to handle entity changes
     * @param guid Id of Entity just changed
     */
    protected abstract async onEntityChanged(guid: string): Promise<any>;
    protected unsubscribeAll() {
        this._subcribeHandlers.forEach((_handler, guid) => this.unsubscribeEntity(guid));
        this._subcribeHandlers.clear();
    }
    public unsubscribeEntity(guid: string): void {
        const handler = this._subcribeHandlers.get(guid);
        MendixApi.unsubscribe(handler);
        this._subcribeHandlers.delete(guid);
    }
}
